﻿import { store } from '@redux/store';
import GoogleAnalyticsCustomDimensions from 'constants/googleAnalyticsCustomDimension';
import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import { userProfileActions } from 'features/userProfile';
import { closeButton } from '../constants/sharedHtml';
import leadConstructor from '../services/leadConstructor';
import { postLeadInformationFormBasic } from '../services/taskFlow';
import getBasicPrivacyPolicyParagraph from '../services/basicPrivacyPolicyParagraph';
import CommentsTextArea from './commentsTextArea';
import EmailInput from './emailInput';
import InformationButtonBasic from './informationButtonBasic';
import NameInput from './nameInput';
import PhoneInput from './phoneInput';
import ZipInput from './zipInput';

/* Creates a new instance of a Get More Information Basic Form to allow users getting in touch with an agent
that will lead them into the home buying process. */
export default class InformationFormBasic {
  constructor() {
    const state = store.getState();
    const userProfile = state?.userProfile || {};
    this.id = state?.uiState?.modalInformation.id;
    this.type = state?.uiState?.modalInformation.type;
    this.fromPage = state?.uiState.modalInformation.fromPage;
    this.excludeRecommendations = true;
    this.moreInformationButton = new InformationButtonBasic();
    this.commentsTextArea = new CommentsTextArea();
    this.emailAddressInput = new EmailInput();
    this.emailAddressInput.value = userProfile.email;
    this.formId = 'informationFormBasic';
    this.fullNameInput = new NameInput();
    this.fullNameInput.value = userProfile.name;
    this.phoneNumberInput = new PhoneInput();
    this.phoneNumberInput.value = userProfile.phone;
    this.postalCodeInput = new ZipInput();
    this.postalCodeInput.value = userProfile.zip;
    this.postalCodeInput.isInternational = userProfile.livesOutside;
    this.onClickEvent = null;
  }

  /**
   * validateNode: Check if the field has a value as expected.
   * @param {Object} node Input to validate
   * @returns A boolean value indicating whether the input is valid or not.
   */
  static validateNode(node) {
    return node.validate();
  }

  /**
   * getFormOpenedAnalyticsData: Gets the object required for the Google Analytics events on form opened.
   * @param {Object} properties Information regarding to the listing.
   * @returns The Google Analtic event information.
   */
  static getFormOpenedAnalyticsData(properties) {
    const customDimensions = {};

    if (properties.marketId) {
      customDimensions[[GoogleAnalyticsCustomDimensions.MarketId().getName()]] = properties.marketId;
    }

    if (properties.marketName && properties.stateAbbreviation) {
      customDimensions[[GoogleAnalyticsCustomDimensions.MarketName().getName()]] = `${properties.marketName}, ${properties.stateAbbreviation}`;
    }

    if (properties.city) {
      customDimensions[[GoogleAnalyticsCustomDimensions.City().getName()]] = properties.city;
    }
    return new GoogleAnalyticsEvent(
      'Lead Events',
      'Search Results CTA Learn More',
      'Open Form - Learn More',
      customDimensions);
  }

  /**
   * setPrivacyPolicyText: Sets the structure related to the privacy policy text for the form.
   * @param {string} phoneNumber Builder phone number value.
   * @param {string} contactVendorName Contact vendor name value.
   */
  setPrivacyPolicyText(phoneNumber, contactVendorName) {
    const container = document.createElement('div');
    const privacyPolicyParagraph = getBasicPrivacyPolicyParagraph(contactVendorName);
    container.innerHTML = privacyPolicyParagraph;

    this.basicPrivacyPolicyParagraph = privacyPolicyParagraph;

    if (phoneNumber) {
      container.firstChild.innerHTML = `${container.firstChild.innerHTML}`;
      this.basicPrivacyPolicyParagraph = container.firstChild.outerHTML;
    }
  }

  /**
   * submitInformationForm: Process to validate and submit the form.
   * @param {event} event Click event.
   */
  submitInformationForm(event) {
    event.preventDefault();

    const validationFunctions = [
      this.emailAddressInput,
      this.fullNameInput,
      this.postalCodeInput,
      this.phoneNumberInput,
    ];

    const result = validationFunctions.map(InformationFormBasic.validateNode);

    if (result.some((isValid) => !isValid)) {
      return;
    }

    this.moreInformationButton.getNode().disabled = 'disabled';
    const formData = new FormData(this.getForm());
    const leadInformation = Object.fromEntries(formData.entries());

    leadInformation.id = this.id;
    leadInformation.type = `${this.type}-information-basic`;
    leadInformation.excludeRecommendations = this.excludeRecommendations;
    leadInformation.fromPage = this.housingData.fromPage;
    const leadObject = leadConstructor(leadInformation);
    leadObject.setHousingInformation?.(this.housingData);
    
    postLeadInformationFormBasic(leadObject);
  }

  /**
   * Sets the binding required for the form.
   */
  setHousingData(properties={}) {
    this.housingData = properties;
  }

  /**
   * Sets the binding required for the form.
   */
  bindEvents() {
    this.onClickEvent = this.submitInformationForm.bind(this);
    this.phoneNumberInput.bindEvents();
    this.postalCodeInput.bindEvents();
    if (this.fullNameInput.value === '') {
      this.fullNameInput.focus();
    }
    this.moreInformationButton.getNode().addEventListener('click', this.onClickEvent);
    this.phoneNumberInput.setRequired();
    this.bindChangeEvents();
  }

  /**
   * bindChangeEvents: Get the form inputs then subscribe each OnChangeEvent to store and persist their values
   */
  bindChangeEvents() {
    const changeInputs = [
      this.emailAddressInput,
      this.fullNameInput,
      this.postalCodeInput,
      this.phoneNumberInput,
    ];
    changeInputs.forEach(input => input.getOnChangeEvent().subscribe(data => 
      store.dispatch(userProfileActions.update(data))));
  }

  /**
   * Removes the bindings that should not be still using.
   */
  unbindEvents() {
    this.moreInformationButton.getNode().removeEventListener('click', this.onClickEvent);
  }

  /**
   * getForm: Gets the form container 
   * @returns A HTML element related to the form container.
   */
  getForm() {
    return document.getElementById(this.formId);
  }

  /**
   * Gets the HTML structure for the information brochure form basic.
   * @returns The string with the HTML structure.
   */
  getHtml() {
      return `
      <div class="lead-form form--brochure">
      ${closeButton}
      <p class="form__title">Connect with a local agent</p>
      <p></p>
      <form id='${this.formId}'>
        <fieldset>
          <div class="form__field">
            ${this.fullNameInput.getHtml()}
          </div>
          <div class="form__field">
            ${this.emailAddressInput.getHtml()}
          </div>
          <div class="form__field">
            ${this.phoneNumberInput.getHtml()}
          </div>
          <div class="form__field field--split">
            ${this.postalCodeInput.getHtml()}
          </div>
          <div class="form__field">
            ${this.commentsTextArea.getHtml()}
          </div>
          <div class="form__button-bar">
            ${this.moreInformationButton.getHtml()}
          </div>
        </fieldset>        
      </form>
      ${this.basicPrivacyPolicyParagraph}
    </div>`;
  }
}